import {
  ChartType,
  ProjectSite,
  ProjectCriteria,
  ReportSamplingDto,
} from '@/store/modules/projects/types/projects.types';
import {
  primaryChartColor,
  labelSplitIndex,
} from '@/store/types/general.types';
import { cloneDeep as _cloneDeep, get as _get, map as _map } from 'lodash';
import { CRITERION_TYPE } from '@/store/types/criterions.types';
import Chart from 'chart.js';
import dayjs from 'dayjs';
import {
  Document,
  Footer,
  Header,
  HeadingLevel,
  IRunOptions,
  Media,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { IOpAttributes } from 'quill-delta-to-html/dist/commonjs/OpAttributeSanitizer';
import {
  getAdHocSampling,
  getConsecutiveMinMaxSampling,
  getDefaultSampling,
} from '@/components/reports/utils/ReportSampling.util';

export class GenerateSingleSiteDocx {
  public docxFontSize: any = {
    default: 18,
    small: 14,
    big: 28,
    huge: 40,
  };
  public docxColor = {
    primary: '#1a538d',
    danger: 'red',
    warning: 'yellow',
  };
  public newLine: Paragraph = new Paragraph({
    text: '',
    heading: HeadingLevel.HEADING_1,
    thematicBreak: true,
  });

  public emptyLine: Paragraph = new Paragraph('');
  public dirtyMonths: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ];

  public async create(
    document: Document,
    result: any,
    payload: any,
    analysisTitle: string,
    jbiLogo: string,
    projectTitle: string,
    projectSites: ProjectSite[],
    projectCriteria: ProjectCriteria[],
  ) {
    return {
      headers: {
        default: new Header({
          children: [await this.getHeader(document), this.emptyLine],
        }),
      },
      children: [
        ...this.getTitleSection(projectTitle),
        ...this.getSubTitleSection(analysisTitle),
        ...this.getSiteSection(projectSites, payload),
        ...this.getDataCollectionPeriodCriteriaSection(
          projectCriteria,
          payload,
        ),
        ...this.selectedPeriodSection(
          document,
          result,
          payload,
          analysisTitle,
          projectTitle,
          projectSites,
          projectCriteria,
        ),
      ],
      footers: {
        default: new Footer({
          children: [await this.getFooter(), this.emptyLine],
        }),
      },
    };
  }

  public getTitleSection(projectTitle: string): Paragraph[] {
    return [
      new Paragraph({
        children: [
          this.createTextRun({
            text: projectTitle.replace(/\s+/g, ' ').toUpperCase(),
            bold: true,
            size: this.docxFontSize.huge,
            color: '#00205B',
          }),
        ],
      }),
      this.emptyLine,
    ];
  }

  public getSubTitleSection(auditTitle: string): Paragraph[] {
    return [
      new Paragraph({
        children: [
          this.createTextRun({
            text: auditTitle.replace(/\s+/g, ' '),
            bold: true,
            size: this.docxFontSize.big,
            color: 'black',
          }),
        ],
      }),
      this.emptyLine,
    ];
  }

  public getSiteSection(
    projectSites: ProjectSite[],
    payload: any,
  ): Paragraph[] {
    const siteTitleParagraph = new Paragraph({
      children: [
        this.createTextRun({
          text: 'Site(s)',
          bold: true,
          size: this.docxFontSize.default,
          color: '#1D70B8',
        }),
      ],
    });

    const siteListParagraph: Paragraph[] = [];
    const siteNames = this.getSiteList(projectSites, payload);
    siteNames.forEach((siteName) => {
      siteListParagraph.push(this.createBullet(siteName));
    });

    return [siteTitleParagraph, ...siteListParagraph, this.emptyLine];
  }

  public getSiteList(projectSites: ProjectSite[], payload: any): string[] {
    const siteNames: string[] = [];
    payload.filteredSites.forEach((auditSiteMapId: number) => {
      const auditSite: any = projectSites.find((projectSiteDetail) => {
        return projectSiteDetail.id === auditSiteMapId;
      });
      siteNames.push(auditSite.site.name);
    });
    return siteNames;
  }

  public getDataCollectionPeriodCriteriaSection(
    projectCriteria: ProjectCriteria[],
    payload: any,
  ) {
    const criteriaTitleParagraph = new Paragraph({
      children: [
        this.createTextRun({
          text: 'Data Collection Period Criteria',
          bold: true,
          size: this.docxFontSize.default,
          color: '#1D70B8',
        }),
      ],
    });

    const criteriaListParagraph: Paragraph[] = this.getSelectedCriteria(
      projectCriteria,
      payload,
    );

    return [criteriaTitleParagraph, ...criteriaListParagraph, this.emptyLine];
  }

  public getSelectedCriteria(
    projectCriteria: ProjectCriteria[],
    payload: any,
  ): Paragraph[] {
    const criteriaNames: Paragraph[] = [];
    payload.filteredCriterias.forEach((auditCriteriaId: number) => {
      const criteria: any = projectCriteria.find((projectCriteriaDetail) => {
        return projectCriteriaDetail.id === auditCriteriaId;
      });
      criteriaNames.push(this.createBullet(criteria.title));
    });
    return criteriaNames;
  }

  public selectedPeriodSection(
    doc: Document,
    result: any,
    payload: any,
    analysisTitle: string,
    projectTitle: string,
    projectSites: ProjectSite[],
    projectCriteria: ProjectCriteria[],
  ): Paragraph[] {
    const paragraphList: any[] = [];
    const selectedPeriodText: string = this.formatDateRange(
      payload.selectedPeriod,
    );
    paragraphList.push(
      new Paragraph({
        children: [
          this.createTextRun({
            text: 'Data Collection Period',
            bold: true,
            size: this.docxFontSize.default,
            color: '#1D70B8',
          }),
        ],
      }),
      new Paragraph({
        children: [
          this.createTextRun({
            text: selectedPeriodText,
            size: this.docxFontSize.default,
            color: 'black',
          }),
        ],
      }),
      this.emptyLine,
    );

    if (payload.checkIndividualCriteria) {
      const {
        allCharts,
        renderBooleanTables,
        renderBooleanSamplingTables,
        renderMixedTables,
        renderMixedSamplingTables,
      } = this.generateIndividualCriteria(result, payload, projectSites);
      allCharts.forEach((individualChartStackData, individualChartIndex) => {
        const individualChart = individualChartStackData.stack;
        if (individualChart.length > 0) {
          paragraphList.push(
            this.emptyLine,
            new Paragraph({
              children: [
                this.createTextRun({
                  text: individualChart[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: individualChart[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          const image = Media.addImage(
            doc,
            Uint8Array.from(atob(individualChart[1].image), (c) =>
              c.charCodeAt(0),
            ),
            600,
            individualChart[1].height,
          );
          paragraphList.push(new Paragraph(image), this.emptyLine);
        }
        if (renderBooleanTables.length > 0) {
          const renderTable = renderBooleanTables[individualChartIndex];
          const renderBooleanSamplingTable =
            renderBooleanSamplingTables[individualChartIndex];
          const rows: TableRow[] = [];
          if (
            renderTable.stack[0] &&
            renderTable.stack[0].table &&
            renderTable.stack[0].table.body
          ) {
            renderTable.stack[0].table.body.forEach((rowData: any) => {
              const cells: TableCell[] = [];
              rowData.forEach((cellData: any, index: number) => {
                cells.push(
                  new TableCell({
                    width: {
                      size: index === 0 ? 70 : 30,
                      type: WidthType.PERCENTAGE,
                    },
                    children: [
                      new Paragraph({
                        children: [
                          this.createTextRun({
                            text: this.getTableValueText(cellData),
                            bold: !!cellData.bold,
                            size: this.docxFontSize.default,
                            color: cellData.color,
                          }),
                        ],
                      }),
                    ],
                  }),
                );
              });
              rows.push(
                new TableRow({
                  children: cells,
                }),
              );
            });
          }
          const booleanSamplingrows: TableRow[] = [];
          if (
            renderBooleanSamplingTable.stack[1] &&
            renderBooleanSamplingTable.stack[1].table &&
            renderBooleanSamplingTable.stack[1].table.body
          ) {
            renderBooleanSamplingTable.stack[1].table.body.forEach(
              (rowData: any) => {
                const cells: TableCell[] = [];
                rowData.forEach((cellData: any, index: number) => {
                  cells.push(
                    new TableCell({
                      width: {
                        size: index === 0 ? 70 : 30,
                        type: WidthType.PERCENTAGE,
                      },
                      children: [
                        new Paragraph({
                          children: [
                            this.createTextRun({
                              text: this.getTableValueText(cellData),
                              bold: !!cellData.bold,
                              size: this.docxFontSize.default,
                              color: cellData.color,
                            }),
                          ],
                        }),
                      ],
                    }),
                  );
                });
                booleanSamplingrows.push(
                  new TableRow({
                    children: cells,
                  }),
                );
              },
            );
          }
          paragraphList.push(
            new Table({
              rows,
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Paragraph({
              children: [
                this.createTextRun({
                  text: renderBooleanSamplingTable.stack[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: renderBooleanSamplingTable.stack[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Table({
              rows: booleanSamplingrows,
            }),
            this.emptyLine,
          );
        }
      });
      renderMixedTables.forEach(
        (renderTableData: any, renderTableDataIndex: number) => {
          const renderMixedTable = renderTableData;
          const renderMixedSamplingTable =
            renderMixedSamplingTables[renderTableDataIndex];
          paragraphList.push(
            this.emptyLine,
            new Paragraph({
              children: [
                this.createTextRun({
                  text: renderMixedTable.stack[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: renderMixedTable.stack[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          const rows: TableRow[] = [];
          if (
            renderMixedTable.stack[1] &&
            renderMixedTable.stack[1].table &&
            renderMixedTable.stack[1].table.body
          ) {
            renderMixedTable.stack[1].table.body.forEach((rowData: any) => {
              const cells: TableCell[] = [];
              rowData.forEach((cellData: any, index: number) => {
                cells.push(
                  new TableCell({
                    width: {
                      size: index === 0 ? 70 : 30,
                      type: WidthType.PERCENTAGE,
                    },
                    children: [
                      new Paragraph({
                        children: [
                          this.createTextRun({
                            text: this.getTableValueText(cellData),
                            bold: !!cellData.bold,
                            size: this.docxFontSize.default,
                            color: cellData.color,
                          }),
                        ],
                      }),
                    ],
                  }),
                );
              });
              rows.push(
                new TableRow({
                  children: cells,
                }),
              );
            });
          }
          const mixedSamplingRows: TableRow[] = [];
          if (
            renderMixedSamplingTable.stack[1] &&
            renderMixedSamplingTable.stack[1].table &&
            renderMixedSamplingTable.stack[1].table.body
          ) {
            renderMixedSamplingTable.stack[1].table.body.forEach(
              (rowData: any) => {
                const cells: TableCell[] = [];
                rowData.forEach((cellData: any, index: number) => {
                  cells.push(
                    new TableCell({
                      width: {
                        size: index === 0 ? 70 : 30,
                        type: WidthType.PERCENTAGE,
                      },
                      children: [
                        new Paragraph({
                          children: [
                            this.createTextRun({
                              text: this.getTableValueText(cellData),
                              bold: !!cellData.bold,
                              size: this.docxFontSize.default,
                              color: cellData.color,
                            }),
                          ],
                        }),
                      ],
                    }),
                  );
                });
                mixedSamplingRows.push(
                  new TableRow({
                    children: cells,
                  }),
                );
              },
            );
          }
          paragraphList.push(
            new Table({
              rows,
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Paragraph({
              children: [
                this.createTextRun({
                  text: renderMixedSamplingTable.stack[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: renderMixedSamplingTable.stack[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Table({
              rows: mixedSamplingRows,
            }),
            this.emptyLine,
          );
        },
      );
    } else {
      const {
        aggregateChart,
        renderTables,
        renderSamplingTables,
      } = this.generateAggregateCriteria(result, payload, projectSites);

      aggregateChart[0].stack.forEach((stackObject: any) => {
        if (stackObject.image && stackObject.image !== '') {
          const image = Media.addImage(
            doc,
            Uint8Array.from(atob(stackObject.image), (c) => c.charCodeAt(0)),
            600,
            stackObject.height,
          );
          paragraphList.push(new Paragraph(image), this.emptyLine);
        }
      });
      renderTables.forEach(
        (renderTableData: any, renderTableDataIndex: number) => {
          const renderTable = renderTableData;
          const renderSamplingTable =
            renderSamplingTables[renderTableDataIndex];
          paragraphList.push(
            this.emptyLine,
            new Paragraph({
              children: [
                this.createTextRun({
                  text: renderTable.stack[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: renderTable.stack[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          const rows: TableRow[] = [];
          if (
            renderTable.stack[1] &&
            renderTable.stack[1].table &&
            renderTable.stack[1].table.body
          ) {
            renderTable.stack[1].table.body.forEach((rowData: any) => {
              const cells: TableCell[] = [];
              rowData.forEach((cellData: any, index: number) => {
                cells.push(
                  new TableCell({
                    width: {
                      size: index === 0 ? 70 : 30,
                      type: WidthType.PERCENTAGE,
                    },
                    children: [
                      new Paragraph({
                        children: [
                          this.createTextRun({
                            text: this.getTableValueText(cellData),
                            bold: !!cellData.bold,
                            size: this.docxFontSize.default,
                            color: cellData.color,
                          }),
                        ],
                      }),
                    ],
                  }),
                );
              });
              rows.push(
                new TableRow({
                  children: cells,
                }),
              );
            });
          }
          const samplingrows: TableRow[] = [];
          if (
            renderSamplingTable.stack[1] &&
            renderSamplingTable.stack[1].table &&
            renderSamplingTable.stack[1].table.body
          ) {
            renderSamplingTable.stack[1].table.body.forEach((rowData: any) => {
              const cells: TableCell[] = [];
              rowData.forEach((cellData: any, index: number) => {
                cells.push(
                  new TableCell({
                    width: {
                      size: index === 0 ? 70 : 30,
                      type: WidthType.PERCENTAGE,
                    },
                    children: [
                      new Paragraph({
                        children: [
                          this.createTextRun({
                            text: this.getTableValueText(cellData),
                            bold: !!cellData.bold,
                            size: this.docxFontSize.default,
                            color: cellData.color,
                          }),
                        ],
                      }),
                    ],
                  }),
                );
              });
              samplingrows.push(
                new TableRow({
                  children: cells,
                }),
              );
            });
          }
          paragraphList.push(
            new Table({
              rows,
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Paragraph({
              children: [
                this.createTextRun({
                  text: renderSamplingTable.stack[0].text,
                  bold: true,
                  size: this.docxFontSize.default,
                  color: renderSamplingTable.stack[0].color,
                }),
              ],
            }),
            this.emptyLine,
          );
          paragraphList.push(
            new Table({
              rows: samplingrows,
            }),
            this.emptyLine,
          );
        },
      );
    }

    return paragraphList;
  }

  public generateIndividualCriteria(
    result: any,
    payload: any,
    projectSites: ProjectSite[],
  ) {
    const allCharts: any[] = [];
    const renderBooleanTables: any[] = [];
    const renderBooleanSamplingTables: any[] = [];
    const renderMixedTables: any[] = [];
    const renderMixedSamplingTables: any[] = [];
    // generate chart
    const dataSet: any[] = [];
    const dataSetOptions: any[] = [];
    const verticalBarChartDataSetOption: any = {
      animation: {
        duration: 0,
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: 100,
              min: 0,
              fontSize: 20,
            },
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Compliance %',
              display: true,
              fontSize: 20,
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Criteria',
              display: false,
            },
            ticks: {
              fontSize: 20,
              minRotation: 0,
              callback: (label: string, index: number) => {
                if (payload.checkIndividualCriteria) {
                  return this.transformLabel(label);
                } else {
                  if (payload.filteredCriterias.length > 4) {
                    return index + 1 + '.';
                  }
                  return this.transformLabel(label);
                }
              },
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      responsive: true,
      maintainAspectRatio: true,
    };
    const horizontalBarChartDataSetOption: any = {
      animation: {
        duration: 0,
      },
      scales: {
        xAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: 100,
              min: 0,
              fontSize: 20,
            },
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Compliance %',
              display: true,
              fontSize: 20,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Criteria',
              display: false,
            },
            ticks: {
              fontSize: 20,
              minRotation: 0,
              callback: (label: string, index: number) => {
                if (payload.checkIndividualCriteria) {
                  return this.transformLabel(label);
                } else {
                  if (payload.filteredCriterias.length > 4) {
                    return index + 1 + '.';
                  }
                  return this.transformLabel(label);
                }
              },
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      responsive: true,
      maintainAspectRatio: true,
    };
    const {
      aggregateCriteriaConfiguration,
      individualBooleanCriteriaConfiguration,
      individualMixedCriteriaConfiguration,
    } = this.generateConfigurationData(
      result.statisticData.configurationData,
      result.statisticData.samplingConfiguration,
    );
    const siteName = projectSites[0].site.name;
    if (result.chartData.length > 0) {
      const chartData = result.chartData;
      Object.keys(chartData).forEach((key) => {
        const data = {
          labels: [] as string[],
          datasets: [] as any[],
        };
        let dataOptions = {};
        const dirtyChartData = chartData[Number(key)];
        const label = dirtyChartData.data[0].site.name;
        const chartValue = dirtyChartData.data[0].compliance;
        const dataSetObject = {
          label: dirtyChartData.criteria.title,
          backgroundColor: [primaryChartColor],
          hoverBackgroundColor: primaryChartColor,
          pointBackgroundColor: 'white',
          borderWidth: 1,
          pointBorderColor: '#249EBF',
          data: [chartValue],
          categoryPercentage: this.getIndividualCriteriaChartCategoryPercentage(
            payload,
          ),
        };
        if (payload.selectedChartType === ChartType.verticalBarChart) {
          dataOptions = _cloneDeep(verticalBarChartDataSetOption);
        } else {
          dataOptions = _cloneDeep(horizontalBarChartDataSetOption);
        }
        data.labels.push(label);
        data.datasets.push(dataSetObject);
        dataSet.push(data);
        dataSetOptions.push(dataOptions);
      });
      dataSet.forEach((individualSet, individualSetIndex) => {
        const renderChart = {
          width: 443,
          height: 250,
          image: '',
          alignment: 'center',
          margin: [0, 0, 0, 30],
          unbreakable: true,
        };
        const canvas = document.createElement('canvas');
        document.body.appendChild(canvas);
        if (payload.selectedChartType === ChartType.verticalBarChart) {
          const verticalChart = new Chart(
            canvas.getContext('2d') as CanvasRenderingContext2D,
            {
              type: 'bar',
              data: {
                labels: individualSet.labels,
                datasets: individualSet.datasets,
              },
              options: dataSetOptions[individualSetIndex],
              plugins: [
                {
                  afterDatasetDraw(chart: any, args: any) {
                    args.meta.data.forEach((element: any) => {
                      if (element._model.base - element._model.y < 0) {
                        const borderWidth = 8;
                        const ctx = chart.ctx;
                        const vm = element._view;
                        const half = vm.width / 2;
                        const left = vm.x - half;
                        const right = vm.x + half;
                        const top = vm.y - 2;
                        const width = right - left;
                        const height =
                          chart.chartArea.bottom - top + borderWidth / 2 - 1;
                        ctx.beginPath();
                        ctx.lineWidth = borderWidth;
                        ctx.strokeStyle = '#000000';
                        ctx.setLineDash([3, 4]);
                        ctx.moveTo(left, top);
                        ctx.lineTo(left, top + height);
                        ctx.moveTo(left, top);
                        ctx.lineTo(left + width, top);
                        ctx.moveTo(left + width, top);
                        ctx.lineTo(left + width, top + height);
                        ctx.stroke();
                        ctx.save();
                      }
                    });
                  },
                },
              ],
            },
          );
          const base64Image = verticalChart.toBase64Image();
          renderChart.image = base64Image.split(',')[1];
        } else {
          const horizontalChart = new Chart(
            canvas.getContext('2d') as CanvasRenderingContext2D,
            {
              type: 'horizontalBar',
              data: {
                labels: individualSet.labels,
                datasets: individualSet.datasets,
              },
              options: dataSetOptions[individualSetIndex],
              plugins: [
                {
                  afterDatasetDraw(chart: any, args: any) {
                    args.meta.data.forEach((element: any) => {
                      if (element._model.base - element._model.x > 0) {
                        const borderWidth = 8;
                        const ctx = chart.ctx;
                        const vm = element._view;
                        const half = vm.height / 2;
                        const top = vm.y - half;
                        const bottom = vm.y + half;
                        const right = vm.x + 10;
                        const left = chart.chartArea.left;
                        ctx.beginPath();
                        ctx.lineWidth = borderWidth;
                        ctx.strokeStyle = '#000000';
                        ctx.setLineDash([4, 5]);
                        ctx.moveTo(left, top);
                        ctx.lineTo(right, top);
                        ctx.moveTo(right, top);
                        ctx.lineTo(right, bottom);
                        ctx.moveTo(right, bottom);
                        ctx.lineTo(left, bottom);
                        ctx.stroke();
                        ctx.save();
                      }
                    });
                  },
                },
              ],
            },
          );
          const base64Image = horizontalChart.toBase64Image();
          renderChart.image = base64Image.split(',')[1];
        }
        const stack: any[] = [renderChart];
        const criteriaChart = {
          stack,
          unbreakable: true,
        };
        allCharts.push(criteriaChart);
        document.body.removeChild(canvas);
      });
    } else {
      allCharts.push({
        stack: [],
        unbreakable: true,
      });
    }
    if (individualBooleanCriteriaConfiguration.length !== 0) {
      // generate boolean criteria tables
      individualBooleanCriteriaConfiguration.forEach(
        (criteriaDetails, criteriaDetailsIndex: number) => {
          const title = {
            text: criteriaDetailsIndex + 1 + '. ' + criteriaDetails.title,
            bold: true,
            color: '#444F51',
            fontSize: 11,
            margin: [0, 0, 0, 17],
          };
          const bodyRows: any[][] = [];
          criteriaDetails.criteriaSamplingData.forEach(
            (optionsData: any, optionDataIndex: number) => {
              const rowData: any[] = [];
              const option = Object.keys(optionsData)[0];
              rowData.push({
                text: option,
                color: '#444F51',
                fontSize: 12,
                border: [true, false, true, false],
                margin:
                  optionDataIndex ===
                  criteriaDetails.criteriaSamplingData.length - 1
                    ? [8, 6, 0, 6]
                    : [8, 6, 0, 0],
              });
              rowData.push({
                text: criteriaDetails.criteriaSamplingDataConfiguration.length
                  ? optionsData[option]
                  : '-',
                color: '#444F51',
                fontSize: 12,
                border: [true, false, true, false],
                alignment: 'right',
                margin:
                  optionDataIndex ===
                  criteriaDetails.criteriaSamplingData.length - 1
                    ? [0, 6, 8, 6]
                    : [0, 6, 8, 0],
              });
              bodyRows.push(rowData);
            },
          );
          let total: number | string = 0;
          let compliance: any = 0;
          bodyRows.forEach((optionValues) => {
            if (optionValues[1].text === '-') {
              total = '-';
            } else {
              total += optionValues[1].text;
            }
          });
          bodyRows.push([
            {
              text: 'Total Data Collected',
              color: '#444F51',
              fontSize: 12,
              margin: [8, 6, 0, 6],
            },
            {
              text: total,
              color: '#444F51',
              fontSize: 12,
              alignment: 'right',
              margin: [0, 6, 8, 6],
            },
          ]);
          if (bodyRows[0][1].text === 0) {
            bodyRows.push([
              {
                text: 'Compliance',
                color: '#444F51',
                fontSize: 12,
                margin: [8, 6, 0, 6],
                bold: true,
              },
              {
                text: compliance + '%',
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                bold: true,
                margin: [0, 6, 8, 6],
              },
            ]);
          } else {
            if (bodyRows[0][1].text === '-') {
              compliance = '-';
            } else {
              compliance =
                (bodyRows[0][1].text / (total - bodyRows[2][1].text)) * 100;
            }
            bodyRows.push([
              {
                text: 'Compliance',
                color: '#444F51',
                fontSize: 12,
                margin: [8, 6, 0, 6],
                bold: true,
              },
              {
                text:
                  compliance !== '-'
                    ? Math.round(compliance * 100) / 100 + '%'
                    : compliance,
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                bold: true,
                margin: [0, 6, 8, 6],
              },
            ]);
          }
          const tableData = {
            table: {
              unbreakable: true,
              widths: ['*', 100],
              body: [
                [
                  {
                    text: 'Answer Choices',
                    color: '#9F9F9F',
                    fontSize: 11,
                    margin: [8, 10, 0, 10],
                  },
                  {
                    text: siteName,
                    color: '#9F9F9F',
                    fontSize: 11,
                    alignment: 'right',
                    margin: [0, 10, 8, 10],
                  },
                ],
                ...bodyRows,
              ],
            },
            layout: {
              hLineWidth: () => {
                return 0.5;
              },
              vLineWidth: () => {
                return 0.5;
              },
              hLineColor: () => {
                return '#DBDBDB';
              },
              vLineColor: () => {
                return '#DBDBDB';
              },
            },
          };
          allCharts[criteriaDetailsIndex].stack.unshift(title);
          const stack: any[] = [tableData];
          const criteriaTable = {
            stack,
            unbreakable: true,
          };
          renderBooleanTables.push(criteriaTable);
        },
      );
      individualBooleanCriteriaConfiguration.forEach(
        (criteriaDetails, criteriaDetailsIndex) => {
          const samplingTitle = {
            text: 'Sampling',
            bold: true,
            color: '#444F51',
            fontSize: 11,
            margin: [0, 13, 0, 17],
          };
          const dirtySamplingTables: any[] = this.getSamplingTables(
            criteriaDetails,
            samplingTitle,
            payload,
            projectSites,
          );
          renderBooleanSamplingTables.push(dirtySamplingTables[0]);
        },
      );
    }
    // generate mixed criteria tables
    if (individualMixedCriteriaConfiguration.length !== 0) {
      individualMixedCriteriaConfiguration.forEach(
        (criteriaDetails, criteriaDetailsIndex: number) => {
          const renderTable: any[] = [];
          const title = {
            text:
              individualBooleanCriteriaConfiguration.length +
              criteriaDetailsIndex +
              1 +
              '. ' +
              criteriaDetails.title,
            bold: true,
            color: '#444F51',
            fontSize: 11,
            margin: [0, 0, 0, 17],
          };
          const bodyRows: any[][] = [];
          criteriaDetails.criteriaSamplingData.forEach(
            (optionsData: any, optionDataIndex: number) => {
              const rowData: any[] = [];
              const option = Object.keys(optionsData)[0];
              rowData.push({
                text: option,
                color: '#444F51',
                fontSize: 12,
                border: [true, false, true, false],
                margin:
                  optionDataIndex ===
                  criteriaDetails.criteriaSamplingData.length - 1
                    ? [8, 6, 0, 6]
                    : [8, 6, 0, 0],
              });
              rowData.push({
                text: criteriaDetails.criteriaSamplingDataConfiguration.length
                  ? optionsData[option]
                  : '-',
                color: '#444F51',
                fontSize: 12,
                border: [true, false, true, false],
                alignment: 'right',
                margin:
                  optionDataIndex ===
                  criteriaDetails.criteriaSamplingData.length - 1
                    ? [0, 6, 8, 6]
                    : [0, 6, 8, 0],
              });
              bodyRows.push(rowData);
            },
          );
          let total: number | string = 0;
          bodyRows.forEach((optionValues) => {
            if (optionValues[1].text === '-') {
              total = '-';
            } else {
              total += optionValues[1].text;
            }
          });
          bodyRows.push([
            {
              text: 'Total Data Collected',
              color: '#444F51',
              fontSize: 12,
              margin: [8, 6, 0, 6],
            },
            {
              text: total,
              color: '#444F51',
              fontSize: 12,
              alignment: 'right',
              margin: [0, 6, 8, 6],
            },
          ]);
          const tableData = {
            table: {
              unbreakable: true,
              widths: ['*', 100],
              body: [
                [
                  {
                    text: 'Answer Choices',
                    color: '#9F9F9F',
                    fontSize: 11,
                    margin: [8, 10, 0, 10],
                  },
                  {
                    text: siteName,
                    color: '#9F9F9F',
                    fontSize: 11,
                    alignment: 'right',
                    margin: [0, 10, 8, 10],
                  },
                ],
                ...bodyRows,
              ],
            },
            layout: {
              hLineWidth: () => {
                return 0.5;
              },
              vLineWidth: () => {
                return 0.5;
              },
              hLineColor: () => {
                return '#DBDBDB';
              },
              vLineColor: () => {
                return '#DBDBDB';
              },
            },
          };
          const stack: any[] = [title, tableData];
          const criteriaTable = {
            stack,
            unbreakable: true,
          };
          renderMixedTables.push(criteriaTable);
        },
      );
      individualMixedCriteriaConfiguration.forEach(
        (criteriaDetails, criteriaDetailsIndex) => {
          const samplingTitle = {
            text: 'Sampling',
            bold: true,
            color: '#444F51',
            fontSize: 11,
            margin: [0, 13, 0, 17],
          };
          const dirtySamplingTables: any[] = this.getSamplingTables(
            criteriaDetails,
            samplingTitle,
            payload,
            projectSites,
          );
          renderMixedSamplingTables.push(dirtySamplingTables[0]);
        },
      );
    }
    return {
      allCharts,
      renderBooleanTables,
      renderBooleanSamplingTables,
      renderMixedTables,
      renderMixedSamplingTables,
    };
  }

  public generateAggregateCriteria(
    result: any,
    payload: any,
    projectSites: ProjectSite[],
  ) {
    const aggregateChart: any[] = [];
    const renderChart = {
      width: 443,
      height: 250,
      image: '',
      alignment: 'center',
      margin: [0, 0, 0, 30],
      unbreakable: true,
    };
    const renderTables: any[] = [];
    const renderSamplingTables: any[] = [];
    // generate chart
    const dataSet = [];
    const dataSetOptions = [];
    const verticalBarChartDataSetOption: any = {
      animation: {
        duration: 0,
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: 100,
              min: 0,
              fontSize: 20,
            },
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Compliance %',
              display: true,
              fontSize: 20,
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Criteria',
              display: false,
            },
            ticks: {
              fontSize: 20,
              minRotation: 0,
              callback: (label: string, index: number) => {
                if (payload.checkIndividualCriteria) {
                  return this.transformLabel(label);
                } else {
                  if (payload.filteredCriterias.length > 4) {
                    return index + 1 + '.';
                  }
                  return this.transformLabel(label);
                }
              },
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      responsive: true,
      maintainAspectRatio: true,
    };
    const horizontalBarChartDataSetOption: any = {
      animation: {
        duration: 0,
      },
      scales: {
        xAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: 100,
              min: 0,
              fontSize: 20,
            },
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Compliance %',
              display: true,
              fontSize: 20,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              display: true,
              color: '#000000',
            },
            scaleLabel: {
              labelString: 'Criteria',
              display: false,
            },
            ticks: {
              fontSize: 20,
              minRotation: 0,
              callback: (label: string, index: number) => {
                if (payload.checkIndividualCriteria) {
                  return this.transformLabel(label);
                } else {
                  if (payload.filteredCriterias.length > 4) {
                    return index + 1 + '.';
                  }
                  return this.transformLabel(label);
                }
              },
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      responsive: true,
      maintainAspectRatio: true,
    };
    const {
      aggregateCriteriaConfiguration,
      individualBooleanCriteriaConfiguration,
      individualMixedCriteriaConfiguration,
    } = this.generateConfigurationData(
      result.statisticData.configurationData,
      result.statisticData.samplingConfiguration,
    );
    const siteName = projectSites[0].site.name;
    if (result.chartData.length > 0) {
      const chartData = result.chartData;
      const data = {
        labels: [] as string[],
        datasets: [] as any[],
      };
      let dataOptions = {};
      const labels: string[] = [];
      const chartValues: number[] = [];
      const backgroundColors: string[] = [];
      Object.keys(chartData).map((key: string) => {
        labels.push(chartData[Number(key)].criteria.title);
        chartValues.push(chartData[Number(key)].data[0].compliance);
        backgroundColors.push(primaryChartColor);
      });
      const dataSetObject = {
        label: siteName,
        backgroundColor: backgroundColors,
        hoverBackgroundColor: primaryChartColor,
        pointBackgroundColor: 'white',
        borderWidth: 1,
        pointBorderColor: '#249EBF',
        data: chartValues,
        categoryPercentage: this.getAggregateCriteriaChartCategoryPercentage(
          individualBooleanCriteriaConfiguration,
        ),
      };
      let dataOptionsObject;
      if (payload.selectedChartType === ChartType.verticalBarChart) {
        dataOptionsObject = _cloneDeep(verticalBarChartDataSetOption);
      } else {
        dataOptionsObject = _cloneDeep(horizontalBarChartDataSetOption);
      }
      data.labels = _cloneDeep(labels);
      data.datasets = [];
      data.datasets.push(dataSetObject);
      dataOptions = _cloneDeep(dataOptionsObject);
      dataSet.push(data);
      dataSetOptions.push(dataOptions);
      const canvas = document.createElement('canvas');
      document.body.appendChild(canvas);
      if (payload.selectedChartType === ChartType.verticalBarChart) {
        const verticalChart = new Chart(
          canvas.getContext('2d') as CanvasRenderingContext2D,
          {
            type: 'bar',
            data: {
              labels: dataSet[0].labels,
              datasets: dataSet[0].datasets,
            },
            options: dataSetOptions[0],
            plugins: [
              {
                afterDatasetDraw(chart: any, args: any) {
                  args.meta.data.forEach((element: any) => {
                    if (element._model.base - element._model.y < 0) {
                      const borderWidth = 8;
                      const ctx = chart.ctx;
                      const vm = element._view;
                      const half = vm.width / 2;
                      const left = vm.x - half;
                      const right = vm.x + half;
                      const top = vm.y - 2;
                      const width = right - left;
                      const height =
                        chart.chartArea.bottom - top + borderWidth / 2 - 1;
                      ctx.beginPath();
                      ctx.lineWidth = borderWidth;
                      ctx.strokeStyle = '#000000';
                      ctx.setLineDash([3, 4]);
                      ctx.moveTo(left, top);
                      ctx.lineTo(left, top + height);
                      ctx.moveTo(left, top);
                      ctx.lineTo(left + width, top);
                      ctx.moveTo(left + width, top);
                      ctx.lineTo(left + width, top + height);
                      ctx.stroke();
                      ctx.save();
                    }
                  });
                },
              },
            ],
          },
        );
        const base64Image = verticalChart.toBase64Image();
        renderChart.image = base64Image.split(',')[1];
      } else {
        const horizontalChart = new Chart(
          canvas.getContext('2d') as CanvasRenderingContext2D,
          {
            type: 'horizontalBar',
            data: {
              labels: dataSet[0].labels,
              datasets: dataSet[0].datasets,
            },
            options: dataSetOptions[0],
            plugins: [
              {
                afterDatasetDraw(chart: any, args: any) {
                  args.meta.data.forEach((element: any) => {
                    if (element._model.base - element._model.x > 0) {
                      const borderWidth = 8;
                      const ctx = chart.ctx;
                      const vm = element._view;
                      const half = vm.height / 2;
                      const top = vm.y - half;
                      const bottom = vm.y + half;
                      const right = vm.x + 10;
                      const left = chart.chartArea.left;
                      ctx.beginPath();
                      ctx.lineWidth = borderWidth;
                      ctx.strokeStyle = '#000000';
                      ctx.setLineDash([4, 5]);
                      ctx.moveTo(left, top);
                      ctx.lineTo(right, top);
                      ctx.moveTo(right, top);
                      ctx.lineTo(right, bottom);
                      ctx.moveTo(right, bottom);
                      ctx.lineTo(left, bottom);
                      ctx.stroke();
                      ctx.save();
                    }
                  });
                },
              },
            ],
          },
        );
        const base64Image = horizontalChart.toBase64Image();
        renderChart.image = base64Image.split(',')[1];
      }
      const criteriaNamesText: any[] = labels.map((label) => {
        return {
          text: label,
          color: '#444F51',
          margin: [10, 5, 0, 2],
        };
      });
      const names = {
        layout: 'noBorders',
        table: {
          widths: ['*'],
          body: [
            [
              {
                ol: criteriaNamesText,
                fontSize: 9,
                fillColor: '#EFF7FF',
                markerColor: '#444F51',
              },
            ],
          ],
        },
        margin: [10, 0, 0, 23],
      };
      let aggregateStack: any[];
      if (payload.filteredCriterias.length > 4) {
        renderChart.margin = [0, 0, 0, 15];
        aggregateStack = [renderChart, names];
      } else {
        aggregateStack = [renderChart];
      }
      const criteriaChart = {
        stack: aggregateStack,
        unbreakable: false,
      };
      aggregateChart.push(criteriaChart);
      document.body.removeChild(canvas);
    } else {
      aggregateChart.push({
        stack: [],
        unbreakable: false,
      });
    }
    // generate table
    aggregateCriteriaConfiguration.forEach(
      (criteriaDetails, criteriaDetailsIndex) => {
        const title = {
          text: criteriaDetailsIndex + 1 + '. ' + criteriaDetails.title,
          bold: true,
          color: '#444F51',
          fontSize: 11,
          margin: [0, 0, 0, 17],
        };
        const bodyRows: any[][] = [];
        criteriaDetails.criteriaSamplingData.forEach(
          (optionsData: any, optionDataIndex: number) => {
            const rowData: any[] = [];
            const option = Object.keys(optionsData)[0];
            rowData.push({
              text: option,
              color: '#444F51',
              fontSize: 12,
              border: [true, false, true, false],
              margin:
                optionDataIndex ===
                criteriaDetails.criteriaSamplingData.length - 1
                  ? [8, 6, 0, 6]
                  : [8, 6, 0, 0],
            });
            rowData.push({
              text: criteriaDetails.criteriaSamplingDataConfiguration.length
                ? optionsData[option]
                : '-',
              color: '#444F51',
              fontSize: 12,
              border: [true, false, true, false],
              alignment: 'right',
              margin:
                optionDataIndex ===
                criteriaDetails.criteriaSamplingData.length - 1
                  ? [0, 6, 8, 6]
                  : [0, 6, 8, 0],
            });
            bodyRows.push(rowData);
          },
        );
        let total: number | string = 0;
        let compliance: any = 0;
        bodyRows.forEach((optionValues) => {
          if (optionValues[1].text === '-') {
            total = '-';
          } else {
            total += optionValues[1].text;
          }
        });
        bodyRows.push([
          {
            text: 'Total Data Collected',
            color: '#444F51',
            fontSize: 12,
            margin: [8, 6, 0, 6],
          },
          {
            text: total,
            color: '#444F51',
            fontSize: 12,
            alignment: 'right',
            margin: [0, 6, 8, 6],
          },
        ]);
        if (criteriaDetails.criteriaType === CRITERION_TYPE.BOOLEAN) {
          if (bodyRows[0][1].text === 0) {
            bodyRows.push([
              {
                text: 'Compliance',
                color: '#444F51',
                fontSize: 12,
                margin: [8, 6, 0, 6],
                bold: true,
              },
              {
                text: compliance + '%',
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                bold: true,
                margin: [0, 6, 8, 6],
              },
            ]);
          } else {
            if (bodyRows[0][1].text === '-') {
              compliance = '-';
            } else {
              compliance =
                (bodyRows[0][1].text / (total - bodyRows[2][1].text)) * 100;
            }
            bodyRows.push([
              {
                text: 'Compliance',
                color: '#444F51',
                fontSize: 12,
                margin: [8, 6, 0, 6],
                bold: true,
              },
              {
                text:
                  compliance !== '-'
                    ? Math.round(compliance * 100) / 100 + '%'
                    : compliance,
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                bold: true,
                margin: [0, 6, 8, 6],
              },
            ]);
          }
        }
        const tableData = {
          table: {
            unbreakable: true,
            widths: ['*', 100],
            body: [
              [
                {
                  text: 'Answer Choices',
                  color: '#9F9F9F',
                  fontSize: 11,
                  margin: [8, 10, 0, 10],
                },
                {
                  text: siteName,
                  color: '#9F9F9F',
                  fontSize: 11,
                  alignment: 'right',
                  margin: [0, 10, 8, 10],
                },
              ],
              ...bodyRows,
            ],
          },
          layout: {
            hLineWidth: () => {
              return 0.5;
            },
            vLineWidth: () => {
              return 0.5;
            },
            hLineColor: () => {
              return '#DBDBDB';
            },
            vLineColor: () => {
              return '#DBDBDB';
            },
          },
        };
        const stack: any[] = [title, tableData];
        const criteriaTable = {
          stack,
          unbreakable: true,
        };
        renderTables.push(criteriaTable);
      },
    );
    aggregateCriteriaConfiguration.forEach(
      (criteriaDetails, criteriaDetailsIndex) => {
        const samplingTitle = {
          text: 'Sampling',
          bold: true,
          color: '#444F51',
          fontSize: 11,
          margin: [0, 13, 0, 17],
        };
        const dirtySamplingTables: any[] = this.getSamplingTables(
          criteriaDetails,
          samplingTitle,
          payload,
          projectSites,
        );
        renderSamplingTables.push(dirtySamplingTables[0]);
      },
    );
    return { aggregateChart, renderTables, renderSamplingTables };
  }

  public getSamplingTables(
    criteriaDetails: any,
    samplingTitle: any,
    payload: any,
    projectSites: ProjectSite[],
  ) {
    const dirtySamplingTables: any[] = [];
    const bodyRows: any[][] = [];
    const titleHeaderRow: any[] = [];
    const transformedSamplingRows: any[] = [];
    const dirtySamplingRows: any[] = [];
    let totalColumn: number = 0;
    switch (criteriaDetails.samplingConfiguration.auditSamplingType) {
      case 'adHoc':
        titleHeaderRow.push({
          text: 'Site',
          color: '#444F51',
          fontSize: 11,
          margin: [8, 6, 8, 6],
          alignment: 'left',
        });
        titleHeaderRow.push({
          text: 'Sampling',
          color: '#444F51',
          fontSize: 11,
          margin: [8, 6, 8, 6],
          alignment: 'right',
        });
        totalColumn += 2;
        const dirtyAdHocSampling: ReportSamplingDto[] = getAdHocSampling(
          payload.selectedPeriod.isAudit,
          criteriaDetails.isAuditCriteria,
          criteriaDetails,
          projectSites,
        );
        dirtySamplingRows.push(...dirtyAdHocSampling);
        dirtySamplingRows.forEach((samplingData) => {
          const samplingRows: any[] = [];
          samplingRows.push({
            text: samplingData.siteName,
            color: '#444F51',
            fontSize: 12,
            alignment: 'left',
            margin: [8, 6, 8, 6],
          });
          samplingData.values.forEach((value: any) => {
            samplingRows.push({
              text: value,
              color: '#444F51',
              fontSize: 12,
              alignment: 'right',
              margin: [8, 6, 8, 6],
            });
          });
          transformedSamplingRows.push(samplingRows);
        });
        bodyRows.push(titleHeaderRow);
        bodyRows.push(...transformedSamplingRows);
        break;
      case 'consecutive':
        if (
          criteriaDetails.samplingConfiguration.samplingMode === 'minAndMax'
        ) {
          if (payload.selectedPeriod.isAudit) {
            titleHeaderRow.push({
              text: 'Site',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'left',
            });
            titleHeaderRow.push({
              text: 'Min.',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'right',
            });
            titleHeaderRow.push({
              text: 'Max.',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'right',
            });
            totalColumn += 3;
          } else {
            titleHeaderRow.push({
              text: 'Site',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'left',
            });
            titleHeaderRow.push({
              text: 'Sampling',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'right',
            });
            totalColumn += 2;
          }
          const dirtyConsecutiveMinMaxSampling: ReportSamplingDto[] = getConsecutiveMinMaxSampling(
            payload.selectedPeriod.isAudit,
            criteriaDetails.isAuditCriteria,
            criteriaDetails,
            projectSites,
          );
          dirtySamplingRows.push(...dirtyConsecutiveMinMaxSampling);
          dirtySamplingRows.forEach((samplingData) => {
            const samplingRows: any[] = [];
            samplingRows.push({
              text: samplingData.siteName,
              color: '#444F51',
              fontSize: 12,
              alignment: 'left',
              margin: [8, 6, 8, 6],
            });
            samplingData.values.forEach((value: any) => {
              samplingRows.push({
                text: value,
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                margin: [8, 6, 8, 6],
              });
            });
            transformedSamplingRows.push(samplingRows);
          });
          bodyRows.push(titleHeaderRow);
          bodyRows.push(...transformedSamplingRows);
        } else {
          if (payload.selectedPeriod.isAudit) {
            titleHeaderRow.push({
              text: 'Site',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'left',
            });
            titleHeaderRow.push({
              text: 'Target',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'right',
            });
            totalColumn += 2;
          } else {
            titleHeaderRow.push({
              text: 'Site',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'left',
            });
            titleHeaderRow.push({
              text: 'Sampling',
              color: '#444F51',
              fontSize: 11,
              margin: [8, 6, 8, 6],
              alignment: 'right',
            });
            totalColumn += 2;
          }
          const dirtyDefaultConsecutiveSamplingRowsData: ReportSamplingDto[] = getDefaultSampling(
            payload.selectedPeriod.isAudit,
            criteriaDetails.isAuditCriteria,
            criteriaDetails,
            projectSites,
          );
          dirtySamplingRows.push(...dirtyDefaultConsecutiveSamplingRowsData);
          dirtySamplingRows.forEach((samplingData) => {
            const samplingRows: any[] = [];
            samplingRows.push({
              text: samplingData.siteName,
              color: '#444F51',
              fontSize: 12,
              alignment: 'left',
              margin: [8, 6, 8, 6],
            });
            samplingData.values.forEach((value: any) => {
              samplingRows.push({
                text: value,
                color: '#444F51',
                fontSize: 12,
                alignment: 'right',
                margin: [8, 6, 8, 6],
              });
            });
            transformedSamplingRows.push(samplingRows);
          });
          bodyRows.push(titleHeaderRow);
          bodyRows.push(...transformedSamplingRows);
        }
        break;
      default:
        if (payload.selectedPeriod.isAudit) {
          titleHeaderRow.push({
            text: 'Site',
            color: '#444F51',
            fontSize: 11,
            margin: [8, 6, 8, 6],
            alignment: 'left',
          });
          titleHeaderRow.push({
            text: 'Target',
            color: '#444F51',
            fontSize: 11,
            margin: [8, 6, 8, 6],
            alignment: 'right',
          });
          totalColumn += 2;
        } else {
          titleHeaderRow.push({
            text: 'Site',
            color: '#444F51',
            fontSize: 11,
            margin: [8, 6, 8, 6],
            alignment: 'left',
          });
          titleHeaderRow.push({
            text: 'Sampling',
            color: '#444F51',
            fontSize: 11,
            margin: [8, 6, 8, 6],
            alignment: 'right',
          });
          totalColumn += 2;
        }
        const dirtyDefaultSamplingRowsData: ReportSamplingDto[] = getDefaultSampling(
          payload.selectedPeriod.isAudit,
          criteriaDetails.isAuditCriteria,
          criteriaDetails,
          projectSites,
          criteriaDetails.samplingConfiguration.samplingMode === 'siteBased',
        );
        dirtySamplingRows.push(...dirtyDefaultSamplingRowsData);
        dirtySamplingRows.forEach((samplingData) => {
          const samplingRows: any[] = [];
          samplingRows.push({
            text: samplingData.siteName,
            color: '#444F51',
            fontSize: 12,
            alignment: 'left',
            margin: [8, 6, 8, 6],
          });
          samplingData.values.forEach((value: any) => {
            samplingRows.push({
              text: value,
              color: '#444F51',
              fontSize: 12,
              alignment: 'right',
              margin: [8, 6, 8, 6],
            });
          });
          transformedSamplingRows.push(samplingRows);
        });
        bodyRows.push(titleHeaderRow);
        bodyRows.push(...transformedSamplingRows);
    }
    const samplingTableData = {
      table: {
        unbreakable: true,
        widths: [...Array(totalColumn).keys()].map((temp) => '*'),
        body: [...bodyRows],
      },
      layout: {
        hLineWidth: () => {
          return 0.5;
        },
        vLineWidth: () => {
          return 0.5;
        },
        hLineColor: () => {
          return '#DBDBDB';
        },
        vLineColor: () => {
          return '#DBDBDB';
        },
      },
    };
    const stack: any[] = [samplingTableData];
    const criteriaSamplingTable = {
      stack,
      unbreakable: true,
    };
    dirtySamplingTables.push(criteriaSamplingTable);
    const divider = {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 15,
          x2: 445,
          y2: 15,
          lineWidth: 0.5,
          lineColor: '#DBDBDB',
        },
      ],
      margin: [0, 0, 0, 13],
    };
    dirtySamplingTables[0].stack.unshift(samplingTitle);
    dirtySamplingTables[0].stack.push(divider);
    return dirtySamplingTables;
  }

  public generateConfigurationData(
    configurationData: any[],
    samplingConfiguration: any,
  ) {
    let aggregateCriteriaConfiguration: any[];
    if (Array.isArray(configurationData) && configurationData.length > 0) {
      const aggregateConfig: any = configurationData.map((data: any) => {
        const criteriaOptionsDataDistribution =
          data.criteriaSamplingData.criteriaOptionsDataDistribution;
        const criteriaSamplingData = Object.keys(
          criteriaOptionsDataDistribution,
        ).map((key: string) => ({
          [key]: data.criteriaSamplingData.criteriaOptionsDataDistribution[key],
        }));
        return {
          title: _get(data, 'projectCriteria.title', ''),
          criteriaType: _get(data, 'projectCriteria.criteriaType', ''),
          sampleSize: Number(
            _get(data, 'criteriaSamplingDataConfigurations.sampleSize', 0),
          ),
          siteSamplingConfiguration: data.siteSamplingDataConfigurations,
          criteriaSamplingDataConfiguration:
            data.criteriaSamplingDataConfigurations,
          samplingConfiguration,
          criteriaSamplingData,
          isAuditCriteria: data.isAuditCriteria,
        };
      });
      aggregateCriteriaConfiguration = _cloneDeep(aggregateConfig);
    } else {
      aggregateCriteriaConfiguration = [];
    }
    const {
      individualBooleanCriteriaConfiguration,
      individualMixedCriteriaConfiguration,
    } = this.generateIndividualCriteriaSet(aggregateCriteriaConfiguration);
    return {
      aggregateCriteriaConfiguration,
      individualBooleanCriteriaConfiguration,
      individualMixedCriteriaConfiguration,
    };
  }

  public generateIndividualCriteriaSet(aggregateCriteriaConfiguration: any[]) {
    const individualBooleanCriteriaConfiguration = aggregateCriteriaConfiguration.filter(
      (item) => {
        return item.criteriaType === CRITERION_TYPE.BOOLEAN;
      },
    );
    const individualMixedCriteriaConfiguration = aggregateCriteriaConfiguration.filter(
      (item) => {
        return item.criteriaType !== CRITERION_TYPE.BOOLEAN;
      },
    );
    return {
      individualBooleanCriteriaConfiguration,
      individualMixedCriteriaConfiguration,
    };
  }

  public getAggregateCriteriaChartCategoryPercentage(
    individualBooleanCriteriaConfiguration: any[],
  ): number {
    const noOfBooleanCriterias: number =
      individualBooleanCriteriaConfiguration.length;
    return noOfBooleanCriterias <= 10 ? noOfBooleanCriterias * 0.1 : 0.8;
  }

  public getIndividualCriteriaChartCategoryPercentage(payload: any): number {
    if (payload.selectedChartType === ChartType.verticalBarChart) {
      return 0.1;
    } else {
      return 0.2;
    }
  }

  public transformLabel(label: string): string | string[] {
    const words = label.split(' ');
    let endIndex: number = labelSplitIndex;
    let eachLine: string = '';
    const eachLabel: string[] = [];
    _map(words, (word: string, wordIndex: string) => {
      switch (Number(wordIndex)) {
        case 0:
          eachLine = word;
          break;
        case endIndex:
          eachLabel.push(eachLine);
          eachLine = word;
          endIndex += labelSplitIndex;
          break;
        case words.length - 1:
          eachLine += ' ' + word;
          eachLabel.push(eachLine);
          break;
        default:
          eachLine += ' ' + word;
      }
    });
    if (eachLabel.length >= 3) {
      eachLabel[2] = eachLabel[2] + '...';
      return eachLabel.slice(0, 3);
    }
    return label;
  }

  public formatDateRange(dateObject: any): string {
    const startDate = new Date(dateObject.startedAt);
    let endDate = dateObject.endedAt;
    if (endDate === null) {
      endDate = dayjs().endOf('day').format();
    }
    return this.getDate(startDate) + ' - ' + this.getDate(new Date(endDate));
  }

  public getDate(newValue: Date): string {
    return (
      newValue.getDate() +
      ' ' +
      this.dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public createTextRun(
    params: IRunOptions & {
      deltaAttributes?: IOpAttributes;
      type?: string;
    },
  ) {
    const extraParams = {};

    if (params && params.deltaAttributes) {
      const { deltaAttributes } = params;
      Object.assign(extraParams, { italics: Boolean(deltaAttributes.italic) });
      Object.assign(extraParams, { bold: Boolean(deltaAttributes.bold) });
      Object.assign(extraParams, {
        superScript: Boolean(deltaAttributes.script === 'super'),
      });
      Object.assign(extraParams, {
        subScript: Boolean(deltaAttributes.script === 'sub'),
      });

      if (deltaAttributes.underline) {
        Object.assign(extraParams, { underline: {} });
      } else if (deltaAttributes.size) {
        Object.assign(extraParams, {
          size:
            this.docxFontSize[deltaAttributes.size] ||
            this.docxFontSize.default,
        });
      }
    }

    return new TextRun({
      size: this.docxFontSize.default,
      ...params,
      ...extraParams,
      font: {
        name: 'Helvetica',
      },
    });
  }

  public createBullet(text: string): Paragraph {
    return new Paragraph({
      text,
      bullet: {
        level: 0,
      },
    });
  }

  public async getHeader(document: Document) {
    const blob: any = await fetch(
      `${process.env.VUE_APP_FRONTEND_URL}/assets/images/JBI_banner_report.png`,
    ).then((r) => r.blob());
    const image = Media.addImage(document, blob, 625, 48);
    return new Paragraph(image);
  }

  public getFooter() {
    const exportDateTime = dayjs().format('D MMM YYYY, HH:mm A');
    return new Paragraph(`Exported on ${exportDateTime}`);
  }

  public getTableValueText(cellData: any) {
    return Object.keys(cellData).length > 0
      ? cellData.text
        ? cellData.text.toString()
        : '0'
      : '';
  }
}
